import { Injectable } from '@angular/core'
import { $app } from '@app/app.service'
import { Doodle, DoodleParticipant } from '@data/model/doodle.model'
import { EventParticipant, EventProps } from '@data/model/event.model'
import { MailingList } from '@data/model/mailing-list.model'
import { Partner, PartnerCompany, PartnerUser } from '@data/model/partner.model'
import { Project } from '@data/model/project.model'
import { TablePeriods } from '@data/model/table-periods.model'
import { Topic, TopicComment } from '@data/model/topic.model'
import { InnplaceUser, User } from '@data/model/user.model'
import { EventInput } from '@fullcalendar/angular'

@Injectable()
export class DataService {
   console: boolean = $app.core.modeDevel
   logPrefix: string = '@service - ' + this.constructor.name

   getValue(data: any, path: string, errVal: any, deep: boolean = false) {
      if (!data) {
         return errVal
      } else if (!deep) {
         return data.hasOwnProperty(path) ? data[path] : errVal
      } else {
         let find = $app.core.code.findPath(data, path)

         return !find.error ? find.pointer[find.target] : errVal
      }
   }

   //-- Map by model --//

   mapProject(data: any, mode?: string) {
      let element = <Project>{
         id: this.getValue(data, 'id', 0),
         title: this.getValue(data, 'title', ''),
         startDate: this.getValue(data, 'start_date', ''),
         endDate: this.getValue(data, 'end_date', ''),
         grantAgreementID: this.getValue(data, 'grant_agreement_id', ''),
         grantScheme: this.getValue(data, 'grant_scheme', ''),
         typeOfAction: this.getValue(data, 'type_of_action/value', '', true),
         logo: this.getValue(data, 'logo', ''),
      }

      if (mode == 'dashboard') {
         let responsibleData = this.getValue(data, 'coordinator', null)

         element = <Project>{
            status: this.getValue(data, 'status', 0),
            duration: this.getValue(data, 'duration', 0),
            responsible: responsibleData ? this.mapUser(responsibleData, 'responsible') : null,
            events: [],
            documents: this.getValue(data, 'documents', []).sort(),
            latestDoodle: null,
            folders: this.getValue(data, 'folders', []),
            creationDate: this.getValue(data, 'creationTimestamp', ''),
            ...element,
         }

         let events = this.getValue(data, 'events', [])
         if (events.length) {
            events = events.sort()
            element.events = events.map((row) => this.mapEvent(row))
         }

         let doodles = this.getValue(data, 'doodles', [])
         if (doodles.length) {
            const params = { userMail: $app.core.user.getDataParam('email') }

            element.latestDoodle = <any>this.mapDoodle(doodles[0], 'list', params)
         }
      } else {
         // list

         element = <Project>{
            status: this.getValue(data, 'projectStatus/id', 0, true),
            ...element,
         }
      }

      return element
   }

   mapUser(data: any, mode?: string) {
      let element = <User>{
         id: this.getValue(data, 'id', 0),
         fullname: this.getValue(data, 'fullname', 'Undefined'),
         email: this.getValue(data, 'email', ''),
         partner_id: this.getValue(data, 'partner_id', 0),
         partner_name: this.getValue(data, 'partner_name', 'Undefined'),
      }

      if (mode == 'current') {
         let authorities = []
         for (let row of this.getValue(data, 'roles', [])) {
            if (!row.includes('#CONTACT')) authorities.push(row.toUpperCase())
         }

         element = <User>{
            type: this.getValue(data, 'user_type', ''),
            authorities: authorities,
            ...element,
         }
      }

      return element
   }

   mapInnplaceUser(data: any, mode?: string) {
      return <InnplaceUser>{
         id: this.getValue(data, 'id', 0),
         fullname: this.getUserFullName(data) || 'Undefined',
         email: this.getValue(data, 'email', ''),
      }
   }

   mapPartner(data: any, mode?: string) {
      //var element = null;

      let companyId = this.getValue(data, 'id', -1)

      var element = <Partner>{
         id: -1,
         company: this.getValue(data, 'company', ''),
         company_id: companyId,
         role: this.getValue(data, 'role', 'NULL').toUpperCase(),
         users: [],
      }

      for (let row of this.getValue(data, 'crm_users', [])) {
         let item = <PartnerUser>{
            id: this.getValue(row, 'id', 0),
            fullname: this.getUserFullName(row) || 'Undefined',
            email: this.getValue(row, 'email', ''),
            company_id: companyId,
            userproj_id: this.getValue(row, 'partner_id', 0),
            responsible: this.getValue(row, 'super', false),
            role: element.role,
         }

         element.users.push(item)
      }

      return element
   }

   mapPartnerUser(data: any, mode?: string) {
      //var element = null;

      let companyId = this.getValue(data, 'id', -1)

      var element = <Partner>{
         id: -1,
         company: this.getValue(data, 'company', ''),
         company_id: companyId,
         role: this.getValue(data, 'role', 'NULL').toUpperCase(),
         users: [],
      }

      for (let row of this.getValue(data, 'crm_users', [])) {
         let item = <PartnerUser>{
            id: this.getValue(row, 'id', 0),
            fullname: this.getUserFullName(row) || 'Undefined',
            email: this.getValue(row, 'email', ''),
            company_id: companyId,
            userproj_id: this.getValue(row, 'partner_id', 0),
         }

         element.users.push(item)
      }

      return element
   }

   mapMailingList(data: any, mode?: string) {
      let element = <MailingList>{
         id: this.getValue(data, 'id', 0),
         title: this.getValue(data, 'title', 'Undefined'),
         status: this.getValue(data, 'status', 'ACTIVE'), // ACTIVE, DELETED, HIDED
         totals: {
            users: this.getValue(data, 'users_size', 0),
            partners: this.getValue(data, 'partner_size', 0),
         },
      }

      if (mode == 'detail') {
         let companies = []

         let personsData = this.getValue(data, 'persons', [])
         if (personsData.length) {
            let companyIds = []
            let users = []

            for (let row of personsData) {
               let userData = this.getValue(row, 'crm_user', null)
               let userCompany = userData ? this.getValue(userData, 'company', null) : null
               let companyId = userCompany ? this.getValue(userCompany, 'id', -1) : -1

               if (companyId > -1 && !companyIds.includes(companyId)) {
                  //company

                  companyIds.push(companyId)

                  let company = <PartnerCompany>{
                     id: companyId,
                     name: this.getValue(userCompany, 'name', 'Undefined'),
                     users: [],
                  }

                  companies.push(company)
               }

               if (userData) {
                  //user

                  let user = <PartnerUser>{
                     id: this.getValue(userData, 'id', 0), // crm
                     fullname: this.getUserFullName(userData) || 'Undefined',
                     email: this.getValue(userData, 'email', ''),
                     company_id: companyId,
                     userproj_id: this.getValue(row, 'id', 0),
                  }

                  users.push(user)
               }
            }

            if (companies.length) {
               for (var company of companies) {
                  // set company users
                  for (var user of users) {
                     if (company.id == user.company_id) company.users.push(user)
                  }
               }
            }
         }

         // add element params
         element = <MailingList>{
            companies: companies,
            ...element,
         }
      }

      return element
   }

   mapEvent(data: any, mode?: string) {
      let authorData = this.getValue(data, 'author', null)
      let authorCompany = authorData ? this.getValue(authorData, 'company', null) : null
      let permissionType = this.getValue(data, 'permission/type', '', true)
      let dateFirst = this.getValue(data, 'date_created', '')
      let dateLast = this.getValue(data, 'last_modified_timestamp', '')
      let extendedProps = this.getValue(data, 'extended_props', null, true)
      let categoryId = this.getValue(extendedProps, 'category/id', 11, true)
      const categoryGroup = $app.getEventCategoryGroup(categoryId)
      let email = this.getValue(authorData, 'email', '')

      let element = <EventInput>{
         id: this.getValue(data, 'id', 0),
         title: this.getValue(data, 'title', 'Undefined'),
         start: this.getValue(data, 'start', ''),
         end: this.getValue(data, 'end', ''),
         allDay: this.getValue(data, 'all_day', false),
         className: 'event-categoryGroup-' + categoryGroup,
         extendedProps: <EventProps>{
            location: this.getValue(extendedProps, 'location', ''),
            description: this.getValue(extendedProps, 'description', ''),
            notify: this.getValue(extendedProps, 'notify', false),
            author_fullname: this.getUserFullName(authorData) || email,
            author_company: this.getValue(authorCompany, 'name', ''),
            author_email: this.getValue(authorData, 'email', ''),
            date: dateFirst,
            date_modified: dateFirst != dateLast ? dateLast : '',
            allowEdit: permissionType == 'WRITE',
            category: categoryId,
         },
      }

      if (mode == 'detail') {
         let participants = []

         for (let row of this.getValue(extendedProps, 'participants', [])) {
            let userData = this.getValue(row, 'crm_user', null)

            let user = <EventParticipant>{
               id: this.getValue(userData, 'id', 0),
               userproj_id: this.getValue(row, 'id', 0),
               fullname: this.getUserFullName(userData) || 'Undefined',
            }

            participants.push(user)
         }

         // add element params
         element.extendedProps.participants = participants
         element.extendedProps.additionalParticipants = this.getValue(extendedProps, 'additionalParticipants', [])
      }

      return element
   }

   mapDoodle(data: any, mode?: string, params?: any) {
      const userMail = params && params.hasOwnProperty('userMail') ? params['userMail'] : null

      let authorData = this.getValue(data, 'author', null)
      let authorCompany = authorData ? this.getValue(authorData, 'company', null) : null
      let authorEmail = this.getValue(authorData, 'email', '')

      let permissionType = this.getValue(data, 'permission/type', '', true)
      if (authorEmail == userMail) permissionType = 'WRITE'

      let element = <Doodle>{
         id: this.getValue(data, 'id', 0),
         title: this.getValue(data, 'title', 'Undefined'),
         description: this.getValue(data, 'description', ''),
         location: this.getValue(data, 'location', ''),
         open: this.getValue(data, 'open', false),
         author_fullname: this.getUserFullName(authorData) || authorEmail,
         author_company: this.getValue(authorCompany, 'name', ''),
         author_email: authorEmail,
         totals: {
            users: this.getValue(data, 'participant_size', 0),
            votes: this.getValue(data, 'total_votes', 0),
         },
         options: this.getValue(data, 'options', []),
         allowEdit: permissionType == 'WRITE',
         eventApproved: null,
         isParticipant: false,
         expired: null,
      }

      if (!element.open) {
         for (let option of element.options) {
            if (option.approved == true) {
               element.eventApproved = option
               break
            }
         }
      } else if (element.options.length) {
         const lastDate = element.options[element.options.length - 1].end

         if (lastDate) element.expired = $app.core.time.isDateBefore(lastDate, null)
      }

      if (mode == 'list') {
         // add element param
         element.isParticipant = this.getValue(data, 'can_vote', false)
      } else if (mode == 'detail') {
         let participants = []

         for (let row of this.getValue(data, 'participants', [])) {
            let userData = this.getValue(row, 'crm_user', null)
            let userCompany = userData ? this.getValue(userData, 'company', null) : null

            let user = <DoodleParticipant>{
               id: this.getValue(userData, 'id', 0),
               userproj_id: this.getValue(row, 'id', 0),
               fullname: this.getUserFullName(userData) || 'Undefined',
               email: this.getValue(userData, 'email', ''),
               company: this.getValue(userCompany, 'name', 0),
               votes: [],
               allowEdit: false,
            }

            for (let option of element.options) {
               for (let vote of option.votes) {
                  if (vote.id == user.userproj_id) user.votes.push(option.id)
               }
            }

            if (userMail == user.email) {
               user.allowEdit = true
               element.isParticipant = true
            }

            participants.push(user)
         }

         let dateFirst = this.getValue(data, 'date_created', '')
         let dateLast = this.getValue(data, 'last_modified_timestamp', '')

         // add element params
         element = <Doodle>{
            date: dateFirst,
            date_modified: dateFirst != dateLast ? dateLast : '',
            expiration_date: this.getValue(data, 'expiration_date', ''),
            participants: participants,
            ...element,
         }
      }

      return element
   }

   mapTopic(data: any, mode?: string) {
      let authorData = this.getValue(data, 'author', null)
      let authorCompany = authorData ? this.getValue(authorData, 'company', null) : null
      let permissionType = this.getValue(data, 'permission/type', '', true)
      let dateFirst = this.getValue(data, 'date', '')
      let dateLast = this.getValue(data, 'last_modified_timestamp', '')
      let email = this.getValue(authorData, 'email', null)

      return <Topic>{
         id: this.getValue(data, 'id', 0),
         topic_title: this.getValue(data, 'topic_title', 'Undefined'),
         author_fullname: this.getUserFullName(authorData) || email,
         author_company: this.getValue(authorCompany, 'name', ''),
         content: this.getValue(data, 'detail', ''),
         date: dateFirst,
         date_modified: dateFirst != dateLast ? dateLast : '',
         deleted: false,
         comments: this.getValue(data, 'comments', 0),
         allowEdit: permissionType == 'WRITE',
      }
   }

   mapTopicComment(data: any, mode?: string) {
      let authorData = this.getValue(data, 'author', null)
      let authorCompany = authorData ? this.getValue(authorData, 'company', null) : null
      let permissionType = this.getValue(data, 'permission/type', '', true)
      let dateFirst = this.getValue(data, 'date_created', '')
      let dateLast = this.getValue(data, 'last_modified_timestamp', '')
      let email = this.getValue(authorData, 'email', null)

      let element = <TopicComment>{
         id: this.getValue(data, 'id', 0),
         topic_id: this.getValue(data, 'topic_id', -1),
         content: this.getValue(data, 'content', ''),
         author_fullname: this.getUserFullName(authorData) || email,
         author_company: this.getValue(authorCompany, 'name', ''),
         date: dateFirst,
         date_modified: dateFirst != dateLast ? dateLast : '',
         deleted: this.getValue(data, 'deleted', false),
         allowEdit: permissionType == 'WRITE',
         children: [],
      }

      for (let row of this.getValue(data, 'replies', [])) {
         element.children.push(this.mapTopicComment(row))
      }

      return element
   }

   mapTablePeriods(data: any) {
      let element = <TablePeriods>{
         id: this.getValue(data, 'id', 0),
         period: this.getValue(data, 'period', ''),
         start_date: this.getValue(data, 'start_date', ''),
         end_date: this.getValue(data, 'end_date', ''),
         start_month: this.getValue(data, 'start_month', ''),
         end_month: this.getValue(data, 'end_month', ''),
         description: this.getValue(data, 'description', ''),
      }
      return element
   }

   //-- Functions --//

   getUserFullName(data: any) {
      let fullname = []
      let firstName = this.getValue(data, 'first_name', '')
      if (firstName) fullname.push(firstName)
      let lastName = this.getValue(data, 'last_name', '')
      if (lastName) fullname.push(lastName)

      return fullname.join(' ')
   }
}

